<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import { onMount, onDestroy, setContext, tick } from 'svelte';
  import { writable } from 'svelte/store';
  import { fly } from 'svelte/transition';
  import { push } from 'svelte-spa-router';
  import {
    SvelteFlow,
    Background,
    Controls,
    MiniMap,
    ViewportPortal,
    type Node as FlowNode,
    type Edge as FlowEdge,
    type NodeTypes,
    type EdgeTypes,
    type Connection,
  } from '@xyflow/svelte';
  import '@xyflow/svelte/dist/style.css';
  import AppShell from '../components/AppShell.svelte';
  import Icon from '../components/Icon.svelte';
  import AutomationNode from '../components/automation/AutomationNode.svelte';
  import AutomationEdgeComponent from '../components/automation/AutomationEdge.svelte';
  import PayloadFieldPicker from '../components/automation/PayloadFieldPicker.svelte';
  import ExpressionInput from '../components/automation/ExpressionInput.svelte';
  import {
    getAutomation,
    updateAutomation,
    listRuns,
    resetSample,
    listVersions,
    restoreVersion,
    testAutomation,
    subscribeExecutionSSE,
    getExecution,
    type ExecutionDetail,
    listCredentials,
    createCredential,
    type ExecutionEvent,
    type CredentialItem,
    type CredentialKind,
    type AutomationDetail,
    type AutomationNode as AutomationNodeT,
    type AutomationEdge,
    type GraphConfig,
    type AutomationRun,
    type NodeType,
    type NodeFieldDef,
    type NodeTypeDef,
    type WorkflowVersionItem,
  } from '../lib/automations-api';
  import { ApiError } from '../lib/api';
  import { nodeCatalog, loadNodeCatalog } from '../lib/node-catalog-store';
  import { fetchPublicDataTable, fetchPublicDataTables, type ApiField } from '../lib/data-tables-api';
  import { fetchFunnels, fetchFunnel } from '../lib/funnels-api';
  import { fetchPersons } from '../lib/persons-api';
  import { fmtDateTime } from '../lib/format';

  let { params }: { params: { id: string } } = $props();

  let data = $state<AutomationDetail | null>(null);
  let loading = $state(true);
  let err = $state<string | null>(null);
  let saving = $state(false);
  let saveMsg = $state<string | null>(null);

  let name = $state('');
  let isActive = $state(false);
  let tab = $state<'editor' | 'runs'>('editor');

  // ── Режим редактора: preview (read-only) ↔ edit (full-screen) ──────────────
  let mode = $state<'preview' | 'edit'>('preview');
  const isEdit = $derived(mode === 'edit');
  const isPreview = $derived(mode === 'preview');
  // Внутрішня вкладка повноекранного редактора: канвас ↔ історія запусків.
  let editTab = $state<'editor' | 'runs'>('editor');
  // Канвас редагується лише на вкладці «Редактор» у full-screen.
  const canEdit = $derived(isEdit && editTab === 'editor');
  // Перегляд історії запусків (борд+полотно) — у прев'ю по tab, у edit по editTab.
  const viewingRuns = $derived(isPreview ? tab === 'runs' : editTab === 'runs');
  function enterEdit() { mode = 'edit'; editTab = 'editor'; }
  function exitEdit() { mode = 'preview'; setEditTab('editor'); }
  /** Перемкнути внутрішню вкладку повноекранного редактора. */
  function setEditTab(t: 'editor' | 'runs') {
    editTab = t;
    if (t === 'runs') {
      void loadRuns();
    } else {
      // повертаємось до редагування — прибираємо перегляд запуску
      viewExec = null;
      nodeRunStatus.set({});
      ndvOpen = false;
    }
  }

  // ── Node Detail View (NDV): INPUT | Параметри | OUTPUT ──────────────────────
  let ndvOpen = $state(false);
  let ndvInput = $state<unknown>(undefined);
  let ndvOutput = $state<unknown>(undefined);
  let ndvStepStatus = $state<'succeeded' | 'failed' | null>(null);
  let ndvStepError = $state<string | null>(null);
  let ndvIoLoading = $state(false);

  // ── Збереження/публікація + перегляд запуску + права панель нод ─────────────
  let savedBaseline = $state(''); // серіалізація стану на момент load/save → для dirty
  let viewExec = $state<ExecutionDetail | null>(null); // ≠null ⇒ режим перегляду запуску
  let paletteOpen = $state(false); // права висувна панель нод/тригерів

  // Svelte Flow 1.x приймає звичайні масиви + `bind:` для двосторонньої
  // реактивності. У Svelte 5 використовуємо $state-arrays.
  let flowNodes = $state<FlowNode[]>([]);
  let flowEdges = $state<FlowEdge[]>([]);

  // Поточний viewport canvas (для drag-from-palette позиціювання).
  let viewport = $state({ x: 0, y: 0, zoom: 1 });
  let canvasEl: HTMLDivElement | null = null;

  // ── Undo / redo (історія GraphConfig як JSON-снапшоти) ──────────────────
  let undoStack = $state<string[]>([]);
  let redoStack = $state<string[]>([]);
  const canUndo = $derived(undoStack.length > 0);
  const canRedo = $derived(redoStack.length > 0);

  function snapshot() {
    if (!data) return;
    const snap = JSON.stringify(flowToConfig());
    if (undoStack[undoStack.length - 1] === snap) return; // coalesce no-op
    undoStack = [...undoStack.slice(-49), snap];
    redoStack = [];
  }
  function applyConfig(cfgJson: string) {
    if (!data) return;
    const cfg = JSON.parse(cfgJson) as GraphConfig;
    data.config = cfg;
    configToFlow(cfg);
    selectedNode = null;
    selectedNodeId = null;
    nodeRunStatus.set({});
  }
  function undo() {
    if (!canEdit || !data || undoStack.length === 0) return;
    const cur = JSON.stringify(flowToConfig());
    const prev = undoStack[undoStack.length - 1]!;
    undoStack = undoStack.slice(0, -1);
    redoStack = [...redoStack, cur];
    applyConfig(prev);
  }
  function redo() {
    if (!canEdit || !data || redoStack.length === 0) return;
    const cur = JSON.stringify(flowToConfig());
    const next = redoStack[redoStack.length - 1]!;
    redoStack = redoStack.slice(0, -1);
    undoStack = [...undoStack, cur];
    applyConfig(next);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && ctxMenu) { closeCtxMenu(); return; }
    const t = e.target as HTMLElement | null;
    if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
    const mod = e.metaKey || e.ctrlKey;
    const k = e.key.toLowerCase();
    if (mod && k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
    else if (mod && (k === 'y' || (k === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    else if (canEdit && mod && k === 'a') { e.preventDefault(); selectAll(); }
    else if (canEdit && mod && k === 'c') { e.preventDefault(); copyNodes(); }
    else if (canEdit && mod && k === 'v') {
      e.preventDefault();
      pasteClipboard(lastCursor ? screenToFlow(lastCursor.x, lastCursor.y) : undefined);
    }
    else if (canEdit && mod && k === 'd') { e.preventDefault(); duplicateNodes(); }
  }

  // ── Auto-layout (BFS-шарування від тригера) ─────────────────────────────
  function autoLayout() {
    if (!data) return;
    snapshot();
    const cfg = flowToConfig();
    const children = new Map<string, string[]>();
    for (const e of cfg.edges) {
      const a = children.get(e.source) ?? [];
      a.push(e.target);
      children.set(e.source, a);
    }
    const trigger = cfg.nodes.find((n) => n.type.startsWith('trigger.')) ?? cfg.nodes[0];
    const layer = new Map<string, number>();
    if (trigger) {
      const q = [trigger.id];
      layer.set(trigger.id, 0);
      while (q.length) {
        const id = q.shift()!;
        const L = layer.get(id)!;
        for (const ch of children.get(id) ?? []) {
          if (!layer.has(ch)) { layer.set(ch, L + 1); q.push(ch); }
        }
      }
    }
    const byLayer = new Map<number, string[]>();
    for (const n of cfg.nodes) {
      const L = layer.get(n.id) ?? 0;
      const a = byLayer.get(L) ?? [];
      a.push(n.id);
      byLayer.set(L, a);
    }
    const X0 = 240, Y0 = 60, DX = 260, DY = 150;
    const pos = new Map<string, [number, number]>();
    for (const [L, ids] of byLayer) ids.forEach((id, i) => pos.set(id, [X0 + i * DX, Y0 + L * DY]));
    flowNodes = flowNodes.map((fn) => {
      const p = pos.get(fn.id);
      return p ? { ...fn, position: { x: p[0], y: p[1] } } : fn;
    });
    if (data) {
      data.config.nodes = data.config.nodes.map((n) => {
        const p = pos.get(n.id);
        return p ? { ...n, position: p } : n;
      });
    }
  }

  // ── Drag-from-palette ────────────────────────────────────────────────────
  function onCanvasDragOver(e: DragEvent) {
    if (!canEdit) return;
    if (e.dataTransfer?.types.includes('application/automation')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }
  function onCanvasDrop(e: DragEvent) {
    if (!canEdit) return;
    const type = e.dataTransfer?.getData('application/automation');
    if (!type) return;
    e.preventDefault();
    const rect = canvasEl?.getBoundingClientRect();
    const x = rect ? (e.clientX - rect.left - viewport.x) / viewport.zoom : 300;
    const y = rect ? (e.clientY - rect.top - viewport.y) / viewport.zoom : 240;
    addNode(type, [Math.round(x), Math.round(y)]);
  }

  // ── Версії ────────────────────────────────────────────────────────────────
  let versionsOpen = $state(false);
  let versions = $state<WorkflowVersionItem[]>([]);
  async function openVersions() {
    versionsOpen = !versionsOpen;
    if (versionsOpen && data) {
      try {
        versions = await listVersions(data.id);
      } catch {
        versions = [];
      }
    }
  }
  async function onRestoreVersion(versionId: string) {
    if (!data) return;
    if (!confirm('Відкотити граф до цієї версії? Поточний стан збережеться як нова версія.')) return;
    try {
      const r = await restoreVersion(data.id, versionId);
      data.config = r.config;
      data.version = r.version;
      configToFlow(r.config);
      captureBaseline();
      selectedNode = null;
      selectedNodeId = null;
      undoStack = [];
      redoStack = [];
      versionsOpen = false;
      saveMsg = `Відкочено (v${r.version})`;
      setTimeout(() => (saveMsg = null), 2500);
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }

  const nodeTypes: NodeTypes = {
    automation: AutomationNode as never,
  };
  const edgeTypes: EdgeTypes = {
    automation: AutomationEdgeComponent as never,
  };

  // Шарити функції видалення з нодою/ребром — щоб ✕-кнопки в них могли
  // викликати delete з підтвердженням. Контекст оголошуємо одразу, навіть
  // якщо delete* ще не задекларовані — setContext приймає посилання, тож
  // порядок не критичний (функція буде в scope коли її викличуть з child).
  setContext<(nodeId: string) => void>('automation:deleteNode', (id: string) => deleteNode(id));
  setContext<(edgeId: string) => void>('automation:deleteEdge', (id: string) => deleteEdge(id));
  // Подвійний клік по ноді → відкрити її налаштування (NDV).
  setContext<(nodeId: string) => void>('automation:openNode', (id: string) => openNodeById(id));
  // Кнопка «+» на вихідному порту ноди: відкрити палітру і одразу прив'язати.
  setContext<(sourceId: string, handle: string) => void>('automation:addNext', (sourceId, handle) => openAddNext(sourceId, handle));
  // Множина "source:handle", у яких вже є вихідне ребро (щоб ховати «+»).
  const outgoingStore = writable<Set<string>>(new Set());
  setContext('automation:outgoing', outgoingStore);
  // Чи зараз режим редагування канвасу (щоб показувати «+» лише там).
  const editingStore = writable(false);
  setContext('automation:editing', editingStore);
  $effect(() => outgoingStore.set(new Set(flowEdges.map((e) => `${e.source}:${e.sourceHandle ?? 'main'}`))));
  $effect(() => editingStore.set(canEdit));
  // Закрили палітру без вибору — скидаємо очікуване з'єднання/позицію.
  $effect(() => { if (!paletteOpen) { pendingConnect = null; pendingNodePos = null; } });

  // Очікуване з'єднання: нова нода з палітри прив'яжеться до цього виходу.
  let pendingConnect = $state<{ source: string; sourceHandle: string } | null>(null);
  function openAddNext(sourceId: string, handle: string) {
    if (!canEdit) return;
    pendingConnect = { source: sourceId, sourceHandle: handle };
    const src = data?.config.nodes.find((n) => n.id === sourceId);
    if (src) pendingNodePos = [src.position[0], src.position[1] + 170];
    paletteOpen = true;
  }

  // ── Тест-пробіг (manual run) + live-статуси нод через SSE ───────────────────
  type RunStatus = 'running' | 'succeeded' | 'failed';
  /** Map<nodeId, RunStatus> — читається з AutomationNode для статус-кільця. */
  const nodeRunStatus = writable<Record<string, RunStatus>>({});
  setContext('automation:nodeStatus', nodeRunStatus);

  let testing = $state(false);
  let testExecId = $state<string | null>(null);
  let testUnsub: (() => void) | null = null;

  function clearRunStatus() {
    nodeRunStatus.set({});
  }

  async function runTest() {
    if (!data || testing) return;
    testing = true;
    saveMsg = null;
    clearRunStatus();
    // Перед тестом зберігаємо граф, щоб виконувалась актуальна версія.
    try {
      const cfg = flowToConfig();
      const r = await updateAutomation(data.id, { name, isActive, config: cfg, version: data.version });
      if (typeof r.version === 'number') data.version = r.version;
      undoStack = [];
      redoStack = [];
    } catch (e) {
      if (e instanceof ApiError && e.code === 'automation.version_conflict') {
        showAlert('Цю автоматизацію змінили деінде. Перезавантажуємо актуальну версію — повторіть тест.');
        await load();
        testing = false;
        return;
      }
      // Не критично для тесту — продовжуємо з тим що на сервері.
    }
    try {
      const { executionId } = await testAutomation(data.id);
      testExecId = executionId;
      subscribeTest(executionId);
    } catch (e) {
      testing = false;
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }

  function subscribeTest(execId: string) {
    testUnsub?.();
    testUnsub = subscribeExecutionSSE(execId, (ev: ExecutionEvent) => {
      if (ev.kind === 'node-start') {
        nodeRunStatus.update((m) => ({ ...m, [ev.nodeId]: 'running' }));
      } else if (ev.kind === 'node-finish') {
        nodeRunStatus.update((m) => ({ ...m, [ev.nodeId]: ev.status }));
      } else if (ev.kind === 'execution-status' && ev.status !== 'running') {
        // Виконання завершилось — закриваємо стрім, оновлюємо історію.
        testUnsub?.();
        testUnsub = null;
        testing = false;
        saveMsg = ev.status === 'succeeded' ? 'Тест виконано успішно' : 'Тест завершився з помилкою';
        setTimeout(() => (saveMsg = null), 3000);
        void loadRuns();
        if (ndvOpen) void loadNodeIo(); // оновити INPUT/OUTPUT панелі відкритої ноди
      }
    });
  }

  // ── NDV: відкриття + підвантаження INPUT/OUTPUT останнього прогону ──────────
  function openNdv() {
    ndvOpen = true;
    void loadNodeIo();
  }
  function closeNdv() {
    ndvOpen = false;
  }

  async function loadNodeIo() {
    if (!data || !selectedNodeId) return;
    // Режим перегляду запуску — дані беремо саме з цього виконання (без fetch).
    if (viewExec) {
      const step = viewExec.steps.find((s) => s.nodeId === selectedNodeId);
      ndvInput = step?.input;
      ndvOutput = step?.output;
      ndvStepStatus = step?.status ?? null;
      ndvStepError = step?.error ?? null;
      return;
    }
    ndvIoLoading = true;
    ndvStepError = null;
    try {
      const recent = await listRuns(data.id, 1); // найновіше виконання
      const exec = recent[0];
      if (!exec) {
        ndvInput = undefined;
        ndvOutput = undefined;
        ndvStepStatus = null;
        return;
      }
      const detail = await getExecution(data.id, exec.id);
      const step = detail.steps.find((s) => s.nodeId === selectedNodeId);
      if (step) {
        ndvInput = step.input;
        ndvOutput = step.output;
        ndvStepStatus = step.status;
        ndvStepError = step.error ?? null;
      } else {
        ndvInput = undefined;
        ndvOutput = undefined;
        ndvStepStatus = null;
      }
    } catch {
      ndvInput = undefined;
      ndvOutput = undefined;
      ndvStepError = 'Не вдалося завантажити дані виконання';
    } finally {
      ndvIoLoading = false;
    }
  }

  // ── Перегляд минулого запуску на read-only полотні ──────────────────────────
  /** Завантажити дані запуску у viewExec + підсвітити ноди (без зміни режиму). */
  async function loadExecutionData(execId: string) {
    if (!data) return;
    try {
      const d = await getExecution(data.id, execId);
      viewExec = d;
      const map: Record<string, 'running' | 'succeeded' | 'failed'> = {};
      for (const s of d.steps) map[s.nodeId] = s.status;
      nodeRunStatus.set(map);
      selectedNode = null;
      selectedNodeId = null;
      ndvOpen = false;
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    }
  }
  /** Вибір запуску з борду (прев'ю або повноекранний редактор). */
  function pickRunInEditor(execId: string) {
    void loadExecutionData(execId);
  }

  // ── Навігація між блоками у NDV (сусідні ноди зліва/справа) ─────────────────
  interface AdjInfo { id: string; label: string; icon: import('../lib/icons').IconName }
  function adjacentInfo(ids: string[]): AdjInfo[] {
    const seen = new Set<string>();
    const out: AdjInfo[] = [];
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      const n = data?.config.nodes.find((x) => x.id === id);
      if (!n) continue;
      const def = $nodeCatalog[n.type];
      out.push({ id, label: def?.label ?? n.type, icon: (def?.icon ?? 'automations') });
    }
    return out;
  }
  /** Попередні блоки (вхідні ребра) для відкритої у NDV ноди. */
  const ndvPrev = $derived.by<AdjInfo[]>(() =>
    selectedNodeId ? adjacentInfo(flowEdges.filter((e) => e.target === selectedNodeId).map((e) => e.source)) : [],
  );
  /** Наступні блоки (вихідні ребра). */
  const ndvNext = $derived.by<AdjInfo[]>(() =>
    selectedNodeId ? adjacentInfo(flowEdges.filter((e) => e.source === selectedNodeId).map((e) => e.target)) : [],
  );
  /** Перейти у NDV до сусіднього блоку. */
  function navigateNdv(nodeId: string) {
    const n = data?.config.nodes.find((x) => x.id === nodeId);
    if (!n) return;
    selectedNodeId = nodeId;
    selectedNode = n;
    void loadNodeIo();
  }

  let selectedNodeId = $state<string | null>(null);
  let selectedNode = $state<AutomationNodeT | null>(null);

  let runs = $state<AutomationRun[]>([]);

  /** Custom-поля з налаштувань "Структура даних". Тут лише не-builtIn —
   *  їх ми додаємо як змінні до відповідних нод (Person/Payment). */
  let personCustomFields = $state<ApiField[]>([]);
  let paymentCustomFields = $state<ApiField[]>([]);

  // Поле, біля якого зараз відкрито field-picker (key з NODE_CATALOG fields).
  let pickerForField = $state<string | null>(null);

  /** Останній прийнятий payload — основа для field-picker і trigger-properties.
   *  Заповнюється БЕКЕНДом при кожному hit'і на webhook URL; UI просто читає. */
  const livePayload = $derived<unknown>(data?.lastWebhookPayload ?? null);

  /** Вихідні ключі, які ноди додають у потік (для пікера змінних попередніх нод). */
  const NODE_OUTPUT_KEYS: Record<string, string[]> = {
    'action.createRecord': ['recordId', 'tableSlug'],
    'action.updateRecord': ['recordId', 'tableSlug'],
    'action.upsertPerson': ['personId'],
    'action.createPayment': ['paymentId', 'personId'],
    'action.emit_event': ['emittedEventId'],
    'action.move_to_stage': ['movedToStage'],
  };
  /** Дані, доступні у виразах: webhook-payload + виходи лише ВИЩЕстоячих (зв'язаних
   *  попередніх) нод — не наступних. Ancestors рахуються зворотним обходом ребер. */
  const availableData = $derived.by<unknown>(() => {
    // Ancestor node output keys (merged into OBJECT payloads only).
    const nodeKeys: Record<string, unknown> = {};
    if (data && selectedNode) {
      const incoming = new Map<string, string[]>();
      for (const e of data.config.edges ?? []) {
        const a = incoming.get(e.target) ?? [];
        a.push(e.source);
        incoming.set(e.target, a);
      }
      const ancestors = new Set<string>();
      const stack = [selectedNode.id];
      while (stack.length) {
        const cur = stack.pop()!;
        for (const src of incoming.get(cur) ?? []) {
          if (!ancestors.has(src)) { ancestors.add(src); stack.push(src); }
        }
      }
      const byId = new Map((data.config.nodes ?? []).map((n) => [n.id, n]));
      for (const id of ancestors) {
        const n = byId.get(id);
        if (!n) continue;
        for (const k of NODE_OUTPUT_KEYS[n.type] ?? []) nodeKeys[k] = `…(${k})`;
      }
    }
    // Preserve the real payload SHAPE. A webhook posting an array `[{…}]` keeps its
    // array shape so the picker shows `$[0].…` paths (previously arrays were dropped
    // → empty → the `$` button stayed disabled).
    if (Array.isArray(livePayload)) return livePayload;
    if (livePayload && typeof livePayload === 'object') return { ...(livePayload as Record<string, unknown>), ...nodeKeys };
    return Object.keys(nodeKeys).length ? nodeKeys : (livePayload ?? null);
  });
  const hasAvailableData = $derived.by(() => {
    const d = availableData;
    if (Array.isArray(d)) return d.length > 0;
    return !!d && typeof d === 'object' && Object.keys(d as object).length > 0;
  });

  // Run details modal — клік на рядок історії запусків відкриває повний JSON.

  // Авто-поллінг: поки sample відсутній (waiting for first event) — щосекунди
  // тягнемо новий стан з бекенду. Як тільки прийшов — зупиняємо. Економно: не
  // поллим коли user на вкладці runs або коли sample вже є.
  let pollTimer = $state<ReturnType<typeof setInterval> | null>(null);
  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      if (!data || data.lastWebhookPayload || tab !== 'editor') {
        stopPolling();
        return;
      }
      void loadSilent();
    }, 2000);
  }
  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  /** Тихий поллінг webhook-sample. ВАЖЛИВО: НЕ замінюємо весь `data` (це стирало
   *  б НЕЗБЕРЕЖЕНІ локальні правки config — табл./поля нод). Підхоплюємо лише
   *  поля webhook-sample, коли вони з'явились. */
  async function loadSilent() {
    if (!data) return;
    try {
      const fresh = await getAutomation(data.id);
      if (fresh.lastWebhookPayload && !data.lastWebhookPayload) {
        data.lastWebhookPayload = fresh.lastWebhookPayload;
        data.lastWebhookReceivedAt = fresh.lastWebhookReceivedAt;
      }
    } catch {
      // тихо ігноруємо — поллінг знов спробує
    }
  }

  const webhookUrl = $derived(
    data ? `${window.location.origin}/api/automations/hooks/${data.webhookToken}` : '',
  );

  async function load() {
    loading = true;
    err = null;
    try {
      const [a, personSchema, paymentSchema] = await Promise.all([
        getAutomation(params.id),
        fetchPublicDataTable('users').catch(() => null),
        fetchPublicDataTable('payments').catch(() => null),
      ]);
      data = a;
      name = a.name;
      isActive = a.isActive;
      configToFlow(a.config);
      captureBaseline();
      // Нова/порожня автоматизація — одразу у режим редагування (центральний «+»).
      if (a.config.nodes.length === 0) {
        mode = 'edit';
        void centerPlaceholderView(); // плейсхолдер у flow(0,0) → центруємо вʼюпорт
      }
      personCustomFields = personSchema?.fields.filter((f) => !f.builtIn) ?? [];
      paymentCustomFields = paymentSchema?.fields.filter((f) => !f.builtIn) ?? [];
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  /** Перенести AutomationNode[] / AutomationEdge[] у Svelte Flow arrays. */
  function configToFlow(cfg: GraphConfig) {
    flowNodes = cfg.nodes.map((n) => ({
      id: n.id,
      type: 'automation',
      position: { x: n.position[0], y: n.position[1] },
      data: {
        nodeType: n.type,
        summary: buildSummary(n),
      },
      deletable: !n.type.startsWith('trigger.'), // будь-який trigger не дозволяємо видаляти
    }));
    flowEdges = cfg.edges.map((e, i) => ({
      id: e.id ?? `e-${i}`,
      type: 'automation',
      source: e.source,
      sourceHandle: e.sourceHandle ?? 'main',
      target: e.target,
      targetHandle: e.targetHandle,
      animated: false,
      style: 'stroke:#7b4fbe; stroke-width:3;',
    }));
  }

  /** Зібрати GraphConfig з поточних Flow arrays для збереження. */
  function flowToConfig(): GraphConfig {
    const map = new Map<string, AutomationNodeT>(
      (data?.config.nodes ?? []).map((n) => [n.id, n]),
    );

    const outNodes: AutomationNodeT[] = flowNodes.map((fn) => {
      const existing = map.get(fn.id);
      const t = (fn.data?.nodeType ?? existing?.type ?? 'trigger.webhook') as NodeType;
      const params = existing?.parameters ?? ({} as Record<string, unknown>);
      return {
        id: fn.id,
        type: t,
        position: [fn.position.x, fn.position.y],
        parameters: params,
      } as AutomationNodeT;
    });

    const outEdges: AutomationEdge[] = flowEdges.map((e) => ({
      id: e.id,
      source: e.source,
      sourceHandle: e.sourceHandle ?? 'main',
      target: e.target,
      targetHandle: e.targetHandle ?? 'main',
    }));

    return { version: 2, nodes: outNodes, edges: outEdges };
  }

  /** Серіалізація поточного стану (граф + назва + активність) — основа для dirty. */
  function serializeState(): string {
    return JSON.stringify(flowToConfig()) + '|' + name + '|' + isActive;
  }
  /** Зафіксувати «збережений» стан (після load/save/restore). */
  function captureBaseline() {
    savedBaseline = serializeState();
  }
  /** Чи є незбережені зміни. */
  const dirty = $derived(savedBaseline !== '' && savedBaseline !== serializeState());
  /** Чи є у графі тригер-нода (визначає центральний «+» і вміст палітри). */
  const hasTrigger = $derived(
    flowNodes.some((n) => String((n.data as { nodeType?: string } | undefined)?.nodeType ?? '').startsWith('trigger.')),
  );

  function buildSummary(n: AutomationNodeT): string {
    if (n.type === 'trigger.webhook') return 'POST → activate';
    if (n.type === 'condition.if' || n.type === 'logic.filter') {
      const p = (n.parameters ?? {}) as { left?: string; operator?: string; right?: string };
      return `${p.left ?? '?'} ${p.operator ?? 'eq'} ${p.right ?? ''}`.trim();
    }
    // upsert/createPayment/інші: показати кілька ключових полів
    const f = (n.parameters ?? {}) as Record<string, unknown>;
    const keys = Object.entries(f)
      .filter(([, v]) => v && String(v).trim())
      .slice(0, 3)
      .map(([k, v]) => `${k}=${v}`);
    return keys.join('\n') || '(порожньо — клік для редагування)';
  }

  function onConnect(_connection: Connection) {
    if (!canEdit) return;
    // SvelteFlow САМ додає ребро (store.addEdge у Handle) ще до onconnect; стиль
    // і тип беруться з defaultEdgeOptions. Тут лише фіксуємо стан для undo —
    // НЕ додаємо вручну, інакше виникає дубль (сіре «привид»-ребро).
    snapshot();
  }

  function addNode(type: NodeType, pos?: [number, number]) {
    if (!canEdit) return;
    snapshot();
    // Позиція: явна (drop) → pendingNodePos (ПКМ «Додати тут») → каскад.
    const at = pos ?? pendingNodePos ?? null;
    pendingNodePos = null;
    const counter = flowNodes.length;
    const x = at ? at[0] : 300 + (counter % 3) * 280;
    const y = at ? at[1] : 240 + Math.floor(counter / 3) * 180;
    const id = `${type}-${Date.now().toString(36)}`;
    const newNode: FlowNode = {
      id,
      type: 'automation',
      position: { x, y },
      data: {
        nodeType: type,
        summary: '(порожньо — клік для редагування)',
      },
    };
    flowNodes = [...flowNodes, newNode];

    // Також зразу додаю у data.config.nodes (щоб flowToConfig зміг знайти parameters).
    if (data) {
      const node: AutomationNodeT = {
        id,
        type,
        position: [x, y],
        parameters: {} as never,
      } as AutomationNodeT;
      data.config.nodes = [...data.config.nodes, node];
    }

    // Якщо додавали з «+» на виході ноди — одразу зʼєднуємо та закриваємо палітру.
    if (pendingConnect) {
      flowEdges = [
        ...flowEdges,
        {
          id: `e-${Date.now().toString(36)}`,
          type: 'automation',
          source: pendingConnect.source,
          sourceHandle: pendingConnect.sourceHandle,
          target: id,
          targetHandle: 'main',
          animated: false,
          style: 'stroke:#7b4fbe; stroke-width:3;',
        } as FlowEdge,
      ];
      pendingConnect = null;
      paletteOpen = false;
    }
  }

  // ── Виділення / копіювання / дублювання / видалення ─────────────────────────
  let clipboard = $state<{ nodes: AutomationNodeT[]; edges: AutomationEdge[] } | null>(null);
  let lastCursor = $state<{ x: number; y: number } | null>(null); // для paste по курсору

  /** Екранні координати → координати полотна (та ж формула, що в onCanvasDrop). */
  function screenToFlow(clientX: number, clientY: number): [number, number] {
    const rect = canvasEl?.getBoundingClientRect();
    const x = rect ? (clientX - rect.left - viewport.x) / viewport.zoom : 300;
    const y = rect ? (clientY - rect.top - viewport.y) / viewport.zoom : 240;
    return [Math.round(x), Math.round(y)];
  }

  /** Ноди, на які діє операція: якщо клік по виділеній — усе виділення, інакше — лише ця. */
  function targetIds(clickedId?: string): string[] {
    const selected = flowNodes.filter((n) => n.selected).map((n) => n.id);
    if (clickedId && !selected.includes(clickedId)) return [clickedId];
    if (selected.length > 0) return selected;
    return clickedId ? [clickedId] : [];
  }

  function isTrigger(id: string): boolean {
    return (data?.config.nodes.find((n) => n.id === id)?.type ?? '').startsWith('trigger.');
  }

  /** Скопіювати ноди (крім тригерів) + внутрішні ребра у буфер. */
  function copyNodes(clickedId?: string) {
    if (!data) return;
    const ids = targetIds(clickedId).filter((id) => !isTrigger(id));
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const nodes = data.config.nodes
      .filter((n) => idSet.has(n.id))
      .map((n) => ({ ...n, parameters: { ...(n.parameters ?? {}) } }));
    const edges = (data.config.edges ?? []).filter((e) => idSet.has(e.source) && idSet.has(e.target));
    clipboard = { nodes, edges };
  }

  /** Вставити буфер: нові id, ребра переприв'язані, зсув (по курсору або +40/+40). */
  function pasteClipboard(at?: [number, number]) {
    if (!data || !clipboard || clipboard.nodes.length === 0) return;
    snapshot();
    const stamp = Date.now().toString(36);
    const idMap = new Map<string, string>();
    clipboard.nodes.forEach((n, i) => idMap.set(n.id, `${n.type}-${stamp}-${i}`));
    // Зсув: щоб ліво-верхня нода опинилась під курсором (або +40,+40 від оригіналу).
    const minX = Math.min(...clipboard.nodes.map((n) => n.position[0]));
    const minY = Math.min(...clipboard.nodes.map((n) => n.position[1]));
    const dx = at ? at[0] - minX : 40;
    const dy = at ? at[1] - minY : 40;

    const newConfigNodes: AutomationNodeT[] = clipboard.nodes.map((n) => ({
      ...n,
      id: idMap.get(n.id)!,
      position: [n.position[0] + dx, n.position[1] + dy],
      parameters: { ...(n.parameters ?? {}) },
    }));
    const newEdges: AutomationEdge[] = clipboard.edges.map((e, i) => ({
      id: `e-${stamp}-${i}`,
      source: idMap.get(e.source)!,
      sourceHandle: e.sourceHandle ?? 'main',
      target: idMap.get(e.target)!,
      targetHandle: e.targetHandle ?? 'main',
    }));

    data.config.nodes = [...data.config.nodes, ...newConfigNodes];
    const newFlowNodes: FlowNode[] = newConfigNodes.map((n) => ({
      id: n.id,
      type: 'automation',
      position: { x: n.position[0], y: n.position[1] },
      data: { nodeType: n.type, summary: buildSummary(n) },
      selected: true,
    }));
    // Зняти попереднє виділення, виділити вставлені.
    flowNodes = [...flowNodes.map((n) => ({ ...n, selected: false })), ...newFlowNodes];
    flowEdges = [
      ...flowEdges,
      ...newEdges.map((e) => ({
        id: e.id,
        type: 'automation',
        source: e.source,
        sourceHandle: e.sourceHandle,
        target: e.target,
        targetHandle: e.targetHandle,
        animated: false,
        style: 'stroke:#7b4fbe; stroke-width:3;',
      }) as FlowEdge),
    ];
  }

  /** Дублювати (copy + paste зі зсувом). */
  function duplicateNodes(clickedId?: string) {
    copyNodes(clickedId);
    pasteClipboard();
  }

  /** Видалити цільові ноди (тригер — ніколи). */
  function deleteTargets(clickedId?: string) {
    const ids = targetIds(clickedId).filter((id) => !isTrigger(id));
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const fns = flowNodes.filter((n) => idSet.has(n.id));
    flowNodes = flowNodes.filter((n) => !idSet.has(n.id));
    onFlowDelete({ nodes: fns, edges: [] });
  }

  /** Виділити всі ноди. */
  function selectAll() {
    flowNodes = flowNodes.map((n) => ({ ...n, selected: true }));
  }

  // ── Контекстне меню полотна/ноди ────────────────────────────────────────────
  let ctxMenu = $state<{ x: number; y: number; kind: 'pane' | 'node'; targetId?: string; flowPos?: [number, number] } | null>(null);
  let pendingNodePos = $state<[number, number] | null>(null); // куди ставити наступну ноду з палітри

  function closeCtxMenu() {
    ctxMenu = null;
  }
  function onPaneContextMenu(e: { event: MouseEvent }) {
    if (!canEdit) return;
    e.event.preventDefault();
    lastCursor = { x: e.event.clientX, y: e.event.clientY };
    ctxMenu = { kind: 'pane', x: e.event.clientX, y: e.event.clientY, flowPos: screenToFlow(e.event.clientX, e.event.clientY) };
  }
  function onNodeContextMenu(e: { node: FlowNode; event: MouseEvent }) {
    if (!canEdit) return;
    e.event.preventDefault();
    ctxMenu = { kind: 'node', x: e.event.clientX, y: e.event.clientY, targetId: e.node.id };
  }
  function onSelectionContextMenu(e: { nodes: FlowNode[]; event: MouseEvent }) {
    if (!canEdit) return;
    e.event.preventDefault();
    ctxMenu = { kind: 'node', x: e.event.clientX, y: e.event.clientY };
  }
  /** «Додати ноду тут» з pane-меню: запамʼятати позицію + відкрити палітру. */
  function addNodeHere() {
    pendingNodePos = ctxMenu?.flowPos ?? null;
    paletteOpen = true;
    closeCtxMenu();
  }
  /** Позиція меню з кліпом у межі вікна. */
  function ctxStyle(m: { x: number; y: number }): string {
    const w = 220, h = 250;
    const x = Math.max(8, Math.min(m.x, window.innerWidth - w - 8));
    const y = Math.max(8, Math.min(m.y, window.innerHeight - h - 8));
    return `left:${x}px; top:${y}px`;
  }
  /** Виконати дію меню і закрити його. */
  function ctxRun(fn: () => void) {
    fn();
    closeCtxMenu();
  }

  /** Обрати ноду (без відкриття налаштувань). */
  function selectNode(nodeId: string) {
    selectedNodeId = nodeId;
    selectedNode = data?.config.nodes.find((n) => n.id === nodeId) ?? null;
  }
  /** Відкрити NDV для ноди (по dblclick або по кліку у прев'ю). */
  function openNodeById(nodeId: string) {
    selectNode(nodeId);
    openNdv();
  }

  function onNodeClick(e: { node: FlowNode }) {
    // Edit: один клік = лише вибір (SvelteFlow сам підсвічує). Прев'ю/перегляд
    // запуску: клік відкриває дані ноди (як було). Відкриття в edit — по dblclick.
    selectNode(e.node.id);
    if (!canEdit) openNdv();
  }

  /** Sync data.config.nodes (де лежать parameters) при будь-якому видаленні
   *  нод/ребер з canvas. Викликається Svelte Flow після Delete-key
   *  ИЛИ нашою програмною ✕-кнопкою / right-click меню. */
  function onFlowDelete(event: { nodes: FlowNode[]; edges: FlowEdge[] }) {
    if (!data) return;
    snapshot();
    if (event.nodes.length > 0) {
      const deletedIds = new Set(event.nodes.map((n) => n.id));
      data.config.nodes = data.config.nodes.filter((n) => !deletedIds.has(n.id));
      // Edges, що посилаються на видалені ноди, прибираємо вручну —
      // bind:edges не робить цього автоматично для всіх кейсів.
      flowEdges = flowEdges.filter((e) => !deletedIds.has(e.source) && !deletedIds.has(e.target));
      if (selectedNode && deletedIds.has(selectedNode.id)) {
        selectedNode = null;
        selectedNodeId = null;
      }
    }
  }

  /** Видалити ноду програмно (з кнопки ✕ на самій ноді). */
  function deleteNode(nodeId: string) {
    if (!canEdit) return;
    const fn = flowNodes.find((n) => n.id === nodeId);
    if (!fn) return;
    // Trigger не дозволяємо видалити — він точка входу.
    if (((fn.data as { nodeType?: NodeType } | undefined)?.nodeType ?? '').startsWith('trigger.')) return;
    const def = $nodeCatalog[fn.data!.nodeType as NodeType];
    if (!confirm(`Видалити ноду "${def?.label ?? fn.data!.nodeType}"? Усі з'єднання до неї теж зникнуть.`)) return;
    flowNodes = flowNodes.filter((n) => n.id !== nodeId);
    onFlowDelete({ nodes: [fn], edges: [] });
  }

  /** Видалити edge — з ✕-кнопки у центрі ребра. Один клік + confirm. */
  function deleteEdge(edgeId: string) {
    if (!canEdit) return;
    snapshot(); // одразу видаляємо (без підтвердження), але undoable
    flowEdges = flowEdges.filter((e) => e.id !== edgeId);
  }

  /**
   * Svelte Flow викликає це ПЕРЕД видаленням (Delete/Backspace на виділеному
   * елементі). Повертаємо false → видалення скасовується.
   * Trigger.webhook завжди не дозволяємо.
   */
  async function onBeforeDelete(event: { nodes: FlowNode[]; edges: FlowEdge[] }): Promise<boolean> {
    if (!canEdit) return false;
    // Захист trigger-ноди.
    const triggerInSelection = event.nodes.some(
      (n) => ((n.data as { nodeType?: NodeType } | undefined)?.nodeType ?? '').startsWith('trigger.'),
    );
    if (triggerInSelection) {
      showAlert('Trigger-ноду видалити не можна — це точка входу автоматизації.');
      return false;
    }
    if (event.nodes.length === 0 && event.edges.length === 0) return false;
    // Видалення лише з'єднань — одразу, без підтвердження.
    if (event.nodes.length === 0) return true;
    const parts: string[] = [];
    if (event.nodes.length === 1) parts.push('1 ноду');
    else if (event.nodes.length > 1) parts.push(`${event.nodes.length} нод`);
    if (event.edges.length === 1) parts.push("1 з'єднання");
    else if (event.edges.length > 1) parts.push(`${event.edges.length} з'єднань`);
    return confirm(`Видалити ${parts.join(' + ')}? Цю дію не можна скасувати.`);
  }

  function onPaneClick() {
    selectedNodeId = null;
    selectedNode = null;
  }

  /** Прочитати сире значення параметра (для boolean/json полів). */
  function readNodeRaw(node: AutomationNodeT, key: string): unknown {
    const params = (node.parameters ?? {}) as Record<string, unknown>;
    if (!key.includes('.')) return params[key];
    const [parent, child] = key.split('.');
    return (params[parent!] as Record<string, unknown> | undefined)?.[child!];
  }

  /** Видимість поля з урахуванням showWhen (умовні поля HTTP/Switch). */
  function isFieldVisible(f: NodeFieldDef): boolean {
    if (!f.showWhen || !selectedNode) return true;
    return readNodeParam(selectedNode, f.showWhen.field) === String(f.showWhen.equals);
  }

  /** JSON-поле → текст для textarea. */
  function jsonText(v: unknown): string {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'string') return v;
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }

  /** Зберегти JSON-поле: валідний JSON → обʼєкт, інакше — сирий текст (помилка на save). */
  function setJsonParam(key: string, text: string): void {
    const t = text.trim();
    if (t === '') {
      updateNodeParam(key, '');
      return;
    }
    try {
      updateNodeParam(key, JSON.parse(t));
    } catch {
      updateNodeParam(key, text);
    }
  }

  function updateNodeParam(key: string, value: unknown) {
    if (!selectedNode || !data) return;
    const idx = data.config.nodes.findIndex((n) => n.id === selectedNode!.id);
    if (idx < 0) return;
    const baseParams = (data.config.nodes[idx]!.parameters ?? {}) as Record<string, unknown>;
    const isEmpty = value === '' || value === undefined;
    // Підтримка dot-notation для nested ключів (customFields.<fieldKey>).
    let nextParams: Record<string, unknown>;
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      const prevChild = (baseParams[parent!] as Record<string, unknown>) ?? {};
      const nextChild = { ...prevChild };
      if (isEmpty) delete nextChild[child!];
      else nextChild[child!] = value;
      nextParams = { ...baseParams, [parent!]: nextChild };
      if (Object.keys(nextChild).length === 0) {
        delete (nextParams as Record<string, unknown>)[parent!];
      }
    } else {
      nextParams = { ...baseParams };
      if (isEmpty) delete nextParams[key];
      else nextParams[key] = value;
    }
    const updated = {
      ...data.config.nodes[idx]!,
      parameters: nextParams,
    } as AutomationNodeT;
    data.config.nodes[idx] = updated;
    selectedNode = updated;

    // Оновити summary у Flow.
    flowNodes = flowNodes.map((n) =>
      n.id === updated.id ? { ...n, data: { ...n.data, summary: buildSummary(updated) } } : n,
    );
  }

  // ── record_fields helpers (field-by-field {fieldKey: value} editor) ──────────
  function recObj(key: string): Record<string, unknown> {
    const v = selectedNode ? readNodeRaw(selectedNode, key) : null;
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  }
  function recAdd(key: string, fieldKey: string) {
    if (!fieldKey) return;
    const obj = recObj(key);
    if (fieldKey in obj) return;
    updateNodeParam(key, { ...obj, [fieldKey]: '' });
  }
  function recSet(key: string, fieldKey: string, value: unknown) {
    updateNodeParam(key, { ...recObj(key), [fieldKey]: value });
  }
  function recRemove(key: string, fieldKey: string) {
    const obj = { ...recObj(key) };
    delete obj[fieldKey];
    updateNodeParam(key, obj);
  }

  /** Усі trigger-ноди з каталогу (для перемикача типу тригера). */
  const triggerDefs = $derived(Object.values($nodeCatalog).filter((d) => d.kind === 'trigger'));

  /** Тригери у правій панелі (з фільтром пошуку). */
  const drawerTriggers = $derived.by(() => {
    const q = paletteSearch.trim().toLowerCase();
    return q
      ? triggerDefs.filter((d) => d.label.toLowerCase().includes(q) || d.type.toLowerCase().includes(q))
      : triggerDefs;
  });
  /** Додати тригер із правої панелі (по центру полотна) + закрити панель. */
  function addTriggerFromDrawer(type: NodeType) {
    addNode(type, [-110, -40]); // нода ~220×80 → центр ≈ flow(0,0), де стоїть плейсхолдер
    paletteOpen = false;
  }

  /** Відцентрувати viewport на flow(0,0) — щоб плейсхолдер «Додати тригер» був по центру. */
  async function centerPlaceholderView() {
    await tick();
    requestAnimationFrame(() => {
      if (!canvasEl) return;
      const r = canvasEl.getBoundingClientRect();
      if (r.width < 50) return;
      viewport = { x: Math.round(r.width / 2), y: Math.round(r.height / 2), zoom: 1 };
    });
  }

  /** Змінити тип trigger-ноди (webhook ↔ schedule ↔ event). Скидає параметри. */
  function changeTriggerType(newType: NodeType) {
    if (!selectedNode || !data || selectedNode.type === newType) return;
    snapshot();
    const idx = data.config.nodes.findIndex((n) => n.id === selectedNode!.id);
    if (idx < 0) return;
    const updated = { ...data.config.nodes[idx]!, type: newType, parameters: {} } as AutomationNodeT;
    data.config.nodes[idx] = updated;
    selectedNode = updated;
    flowNodes = flowNodes.map((n) =>
      n.id === updated.id
        ? { ...n, data: { ...n.data, nodeType: newType, summary: buildSummary(updated) } }
        : n,
    );
  }

  // ── Креденшали (Phase 5b) ──────────────────────────────────────────────────
  let credentials = $state<CredentialItem[]>([]);
  let credFormForField = $state<string | null>(null); // key поля, біля якого відкрита форма
  let credName = $state('');
  let credKind = $state<CredentialKind>('bearer');
  let credData = $state('{\n  "token": ""\n}');
  let credSaving = $state(false);

  async function loadCredentials() {
    try {
      credentials = await listCredentials();
    } catch {
      credentials = [];
    }
  }

  // ── Динамічні пікери для нод (funnel / funnel_stage / person) ──────────────
  let funnelsList = $state<{ slug: string; name: string }[]>([]);
  let stagesByFunnel = $state<Record<string, { slug: string; name: string }[]>>({});
  let personsList = $state<{ id: string; label: string }[]>([]);
  let personSearch = $state('');
  let dataTablesList = $state<{ slug: string; name: string; builtIn: boolean }[]>([]);

  async function loadDataTablesList() {
    try {
      const r = await fetchPublicDataTables();
      dataTablesList = r.items.map((t) => ({ slug: t.slug, name: t.name, builtIn: t.builtIn }));
    } catch {
      dataTablesList = [];
    }
  }

  async function loadFunnelsList() {
    try {
      const items = await fetchFunnels();
      funnelsList = items.map((f) => ({ slug: f.slug, name: f.name }));
    } catch {
      funnelsList = [];
    }
  }
  async function ensureStages(slug: string) {
    if (!slug || stagesByFunnel[slug]) return;
    try {
      const f = await fetchFunnel(slug);
      stagesByFunnel = { ...stagesByFunnel, [slug]: f.stages.map((s) => ({ slug: s.slug, name: s.name })) };
    } catch {
      stagesByFunnel = { ...stagesByFunnel, [slug]: [] };
    }
  }
  // Поля таблиці для 'record_fields'-редактора (lazy, кеш по slug).
  let tableFieldsBySlug = $state<Record<string, ApiField[]>>({});
  async function ensureTableFields(slug: string) {
    if (!slug || tableFieldsBySlug[slug]) return;
    try {
      const t = await fetchPublicDataTable(slug);
      tableFieldsBySlug = { ...tableFieldsBySlug, [slug]: t.fields ?? [] };
    } catch {
      tableFieldsBySlug = { ...tableFieldsBySlug, [slug]: [] };
    }
  }
  // Кастомні події воронки для 'funnel_custom_event'-пікера (lazy, кеш по slug).
  let customEventsByFunnel = $state<Record<string, { key: string; name: string }[]>>({});
  async function ensureCustomEvents(slug: string) {
    if (!slug || customEventsByFunnel[slug]) return;
    try {
      const f = await fetchFunnel(slug);
      customEventsByFunnel = { ...customEventsByFunnel, [slug]: f.customEvents ?? [] };
    } catch {
      customEventsByFunnel = { ...customEventsByFunnel, [slug]: [] };
    }
  }
  async function loadPersonsList() {
    try {
      const r = await fetchPersons({ pageSize: 200, page: 1, sort: 'firstSeenDesc' });
      personsList = r.items.map((p) => ({
        id: p.id,
        label: p.displayName || p.primaryEmail || p.primaryPhone || `p-${p.id.slice(0, 8)}`,
      }));
    } catch {
      personsList = [];
    }
  }
  const filteredPersons = $derived(
    personSearch
      ? personsList.filter((p) => p.label.toLowerCase().includes(personSearch.toLowerCase()))
      : personsList,
  );

  function toggleCredForm(fieldKey: string) {
    credFormForField = credFormForField === fieldKey ? null : fieldKey;
    if (credFormForField) {
      credName = '';
      credKind = 'bearer';
      credData = '{\n  "token": ""\n}';
    }
  }

  async function submitCredential(fieldKey: string) {
    if (!credName.trim() || credSaving) return;
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(credData) as Record<string, unknown>;
    } catch {
      showAlert('Секрет має бути валідним JSON, напр. { "token": "..." }');
      return;
    }
    credSaving = true;
    try {
      const id = await createCredential(credName.trim(), credKind, data);
      await loadCredentials();
      updateNodeParam(fieldKey, id); // одразу обрати новий креденшал
      credFormForField = null;
    } catch (e) {
      showAlert(e instanceof Error ? e.message : String(e));
    } finally {
      credSaving = false;
    }
  }

  /** Підказка JSON-форми секрету залежно від типу. */
  const credPlaceholder: Record<CredentialKind, string> = {
    bearer: '{ "token": "..." }',
    basic: '{ "username": "...", "password": "..." }',
    header: '{ "headerName": "X-API-Key", "headerValue": "..." }',
    apiKeyQuery: '{ "queryName": "api_key", "queryValue": "..." }',
    generic: '{ "headers": { "X-Foo": "bar" } }',
  };

  /** Публікація: перемкнути активність і одразу зберегти (як n8n Publish). */
  async function togglePublish() {
    if (!data || saving) return;
    isActive = !isActive;
    await save();
  }

  async function save() {
    if (!data) return;
    saving = true;
    saveMsg = null;
    try {
      const cfg = flowToConfig();
      const r = await updateAutomation(data.id, { name, isActive, config: cfg, version: data.version });
      if (typeof r.version === 'number') data.version = r.version;
      undoStack = [];
      redoStack = [];
      saveMsg = 'Збережено';
      setTimeout(() => (saveMsg = null), 2500);
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'automation.version_conflict') {
        showAlert('Цю автоматизацію змінили в іншому місці. Сторінку буде перезавантажено з актуальною версією.');
        await load();
      } else {
        saveMsg = e instanceof Error ? e.message : String(e);
      }
    } finally {
      saving = false;
    }
  }

  async function recaptureSample() {
    if (!data) return;
    if (data.lastWebhookPayload && !confirm('Скинути збережений sample? Наступний реальний webhook замінить його структурою.')) return;
    try {
      await resetSample(data.id);
      await load();
      saveMsg = 'Sample очищено — чекаємо новий запит';
      setTimeout(() => (saveMsg = null), 3000);
    } catch (e) {
      saveMsg = e instanceof Error ? e.message : String(e);
    }
  }

  function openPicker(fieldKey: string) {
    pickerForField = pickerForField === fieldKey ? null : fieldKey;
  }

  function pickPath(path: string) {
    if (!pickerForField) return;
    // "fieldKey::subKey" → record_fields nested value; plain key → node param.
    if (pickerForField.includes('::')) {
      const [fk, sub] = pickerForField.split('::');
      recSet(fk!, sub!, path);
    } else {
      updateNodeParam(pickerForField, path);
    }
    pickerForField = null;
  }

  function copyUrl() {
    if (!webhookUrl) return;
    void navigator.clipboard.writeText(webhookUrl);
    saveMsg = 'URL скопійовано';
    setTimeout(() => (saveMsg = null), 2000);
  }

  async function loadRuns() {
    try {
      runs = await listRuns(params.id);
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    }
  }

  /** Парсимо `?run=<id>` з window.location.hash. svelte-spa-router зберігає
   *  query частину після '?' у hash; ми просто dotyагаємо вручну. */
  function getRunIdFromUrl(): string | null {
    const h = typeof window === 'undefined' ? '' : window.location.hash;
    const q = h.split('?')[1];
    if (!q) return null;
    const p = new URLSearchParams(q);
    return p.get('run');
  }

  onMount(async () => {
    window.addEventListener('keydown', onKeydown);
    void loadNodeCatalog(); // підвантажити повний каталог нод (SDK) для палітри/інспектора
    void loadCredentials(); // креденшали для 'credential'-полів
    void loadFunnelsList(); // воронки для 'funnel'/'funnel_stage'-полів
    void loadPersonsList(); // контакти для 'person'-полів
    void loadDataTablesList(); // таблиці для 'data_table'-полів
    await load();
    // Якщо у URL є ?run=<id> — відкриваємо вкладку «Історія запусків» і
    // обираємо цей запуск прямо на борді (полотно з підсвіткою), а не модалку.
    const wantedRunId = getRunIdFromUrl();
    if (wantedRunId) {
      tab = 'runs';
      await loadRuns();
      let found = runs.find((x) => x.id === wantedRunId);
      if (!found) {
        // Якщо у перших 50 не знайшли — підвантажимо до 500.
        try {
          runs = await listRuns(params.id, 500);
          found = runs.find((x) => x.id === wantedRunId);
        } catch {
          // тихо
        }
      }
      // Лише для відомих (нових) запусків — getExecution не знає legacy
      // automation_runs id; борд лишається відкритим без вибору.
      if (found) pickRunInEditor(wantedRunId);
    }
  });

  onDestroy(() => {
    stopPolling();
    testUnsub?.();
    window.removeEventListener('keydown', onKeydown);
  });

  $effect(() => {
    if (tab === 'runs' && data) void loadRuns();
  });

  // Запускати/зупиняти поллінг автоматично — реактивно по livePayload+tab.
  $effect(() => {
    if (data && !data.lastWebhookPayload && tab === 'editor') {
      startPolling();
    } else {
      stopPolling();
    }
  });

  /** Людська назва типу тригера для списку запусків. */
  function triggerLabel(t?: string): string {
    switch (t) {
      case 'webhook': return 'Вебхук';
      case 'manual': return 'Тест';
      case 'schedule': return 'Розклад';
      case 'event': return 'Подія';
      case 'subworkflow': return 'Sub-flow';
      default: return t ?? '—';
    }
  }

  function jsonShort(v: unknown): string {
    if (v === null || v === undefined) return '—';
    try {
      const s = JSON.stringify(v);
      return s.length > 200 ? s.slice(0, 200) + '…' : s;
    } catch {
      return String(v);
    }
  }

  // Пошук у палітрі.
  let paletteSearch = $state('');

  // Палітра — усі ноди з каталогу (крім тригерів), згруповані за категорією,
  // з фільтром за пошуком. Дані з node-catalog-store (генеруються з Node SDK).
  const paletteGroups = $derived.by<{ category: string; items: NodeTypeDef[] }[]>(() => {
    const q = paletteSearch.trim().toLowerCase();
    const entries = Object.values($nodeCatalog).filter((d) => d.kind !== 'trigger');
    const filtered = q
      ? entries.filter((d) => d.label.toLowerCase().includes(q) || d.type.toLowerCase().includes(q))
      : entries;
    const byCat = new Map<string, NodeTypeDef[]>();
    for (const d of filtered) {
      const cat = d.category ?? 'Інше';
      const arr = byCat.get(cat) ?? [];
      arr.push(d);
      byCat.set(cat, arr);
    }
    const order = ['CRM', 'Logic', 'Data', 'Network', 'Flow', 'AI', 'Інше'];
    return [...byCat.entries()]
      .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
      .map(([category, items]) => ({ category, items }));
  });

  // Filtered fields for properties panel — реактивно за вибраним node.
  // Base = поля з NODE_CATALOG (telegramId/email/.../amount/...).
  // Custom = поля з налаштувань (custom_data_fields) для відповідної таблиці.
  // Custom-key має формат "customFields.<fieldKey>" — обробляється updateNodeParam.
  const fieldDefs = $derived.by<NodeFieldDef[]>(() => {
    if (!selectedNode) return [];
    const base = $nodeCatalog[selectedNode.type]?.fields ?? [];
    let custom: ApiField[] = [];
    if (selectedNode.type === 'action.upsertPerson') custom = personCustomFields;
    else if (selectedNode.type === 'action.createPayment') custom = paymentCustomFields;
    const customDefs: NodeFieldDef[] = custom.map((f) => ({
      key: `customFields.${f.fieldKey}`,
      label: f.label,
      hint: '$.шлях або фіксоване значення',
    }));
    return [...base, ...customDefs];
  });

  // Preload data for dependent pickers HERE (not in the template) — calling an async
  // fn inside {#await} on every render created a new promise each time → re-render
  // churn that desynced the parent <select>. The effect runs only when inputs change.
  $effect(() => {
    const n = selectedNode;
    if (!n) return;
    for (const f of fieldDefs) {
      if (f.type === 'record_fields') {
        const s = readNodeParam(n, f.dependsOn ?? 'tableSlug');
        if (s) void ensureTableFields(s);
      } else if (f.type === 'funnel_stage') {
        const s = readNodeParam(n, f.dependsOn ?? 'funnelSlug');
        if (s) void ensureStages(s);
      } else if (f.type === 'funnel_custom_event') {
        const s = readNodeParam(n, f.dependsOn ?? 'funnelSlug');
        if (s) void ensureCustomEvents(s);
      }
    }
  });

  /** Прочитати значення параметра з підтримкою dot-notation (customFields.tag). */
  function readNodeParam(node: AutomationNodeT, key: string): string {
    const params = (node.parameters ?? {}) as Record<string, unknown>;
    if (!key.includes('.')) {
      const v = params[key];
      return v === null || v === undefined ? '' : String(v);
    }
    const [parent, child] = key.split('.');
    const obj = params[parent!] as Record<string, unknown> | undefined;
    const v = obj?.[child!];
    return v === null || v === undefined ? '' : String(v);
  }
</script>

<AppShell active="automations">
  {#if loading}
    <div class="text-sm text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if err || !data}
    <div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {err ?? 'Не знайдено'}
    </div>
  {:else}
    <!-- Індикатор збереження (dirty) + кнопка публікації (Активувати/Вимкнути) -->
    {#snippet savedBadge()}
      {#if dirty}
        <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600" title="Є незбережені зміни"><span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Незбережено</span>
      {:else}
        <span class="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-text-muted)]" title="Усі зміни збережено"><Icon name="check" size={12} /> Збережено</span>
      {/if}
    {/snippet}
    {#snippet publishBtn()}
      <button
        class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors {isActive ? 'border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'text-white'}"
        style={isActive ? '' : 'background: linear-gradient(135deg, #10b981, #059669);'}
        disabled={saving || !data}
        onclick={togglePublish}
        title={isActive ? 'Зупинити — автоматизація стане чернеткою' : 'Активувати — автоматизація почне виконуватись на події/розклад'}
      >{#if isActive}<span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Активна · Вимкнути{:else}<Icon name="play" size={14} /> Активувати{/if}</button>
    {/snippet}

    <!-- Preview header (in AppShell) — read-only до натискання «Редагувати» -->
    {#if isPreview}
    <div class="flex items-center gap-3 mb-4">
      <button
        class="text-xs text-[var(--color-text-muted)] hover:underline"
        onclick={() => push('/automations')}
      >← Назад</button>
      <input
        bind:value={name}
        class="flex-1 rounded-lg border border-transparent hover:border-[var(--color-border-3)] px-2 py-1 text-base font-semibold focus:border-[var(--color-pu)] focus:bg-white"
      />
      {@render savedBadge()}
      {#if saveMsg}<span class="text-xs text-emerald-700">{saveMsg}</span>{/if}
      {#if !viewExec}
        <button
          class="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          disabled={testing || !data}
          onclick={runTest}
          title="Запустити тест із sample-payload (працює і для неактивних)"
        >{#if testing}<Icon name="refresh" size={14} /> Тест…{:else}<Icon name="play" size={14} /> Тест{/if}</button>
        <button
          class="rounded-lg px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
          style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
          disabled={saving || !dirty}
          onclick={save}
        >{#if saving}Збереження…{:else}<span class="inline-flex items-center gap-1.5"><Icon name="save" size={14} /> Зберегти</span>{/if}</button>
      {/if}
      {@render publishBtn()}
      {#if tab === 'editor' && !viewExec}
        <button
          class="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-pu-2)] bg-white px-4 py-2 text-xs font-semibold text-[var(--color-pu-3)] hover:bg-purple-50"
          onclick={enterEdit}
          title="Відкрити повноекранний редактор"
        ><Icon name="edit" size={14} /> Редагувати</button>
      {/if}
    </div>

    <!-- Tabs -->
    <div class="flex gap-2 border-b border-[var(--color-border-3)] mb-3">
      <button
        class="px-4 py-2 text-xs font-semibold border-b-2 -mb-[1px] {tab === 'editor' ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-transparent text-[var(--color-text-muted)]'}"
        onclick={() => { tab = 'editor'; viewExec = null; nodeRunStatus.set({}); ndvOpen = false; }}
      ><span class="inline-flex items-center gap-1.5"><Icon name="wrench" size={13} /> Прев'ю</span></button>
      <button
        class="px-4 py-2 text-xs font-semibold border-b-2 -mb-[1px] {tab === 'runs' ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-transparent text-[var(--color-text-muted)]'}"
        onclick={() => (tab = 'runs')}
      ><span class="inline-flex items-center gap-1.5"><Icon name="audit" size={13} /> Історія запусків</span></button>
    </div>
    {/if}

      {#if isPreview && tab === 'editor' && !viewExec}
      <!-- Webhook URL banner + live status -->
      <div class="flex items-center gap-2 rounded-xl border border-[var(--color-border-3)] bg-blue-50 px-4 py-2 mb-3">
        <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700"><Icon name="automations" size={13} /> Webhook URL:</span>
        <code class="flex-1 text-[11px] font-mono break-all text-blue-900">{webhookUrl}</code>
        {#if data.lastWebhookReceivedAt}
          <span class="text-[10px] text-emerald-700 font-semibold" title="Sample-payload збережено. Наступні запити будуть виконуватись як runs.">
            ● Sample: {fmtDateTime(data.lastWebhookReceivedAt)}
          </span>
          <button
            class="rounded-lg px-3 py-1 text-[10px] font-medium bg-white border border-blue-200 hover:bg-blue-100"
            onclick={recaptureSample}
            title="Скинути sample — наступний реальний webhook замінить його"
          ><span class="inline-flex items-center gap-1.5"><Icon name="refresh" size={12} /> Захопити заново</span></button>
        {:else}
          <span class="inline-flex items-center gap-1.5 text-[10px] text-amber-700 font-semibold animate-pulse"><Icon name="statusPending" size={12} /> Слухаємо… {pollTimer ? '(auto-poll on)' : ''}</span>
          <button
            class="rounded-lg px-3 py-1 text-[10px] font-medium bg-white border border-amber-200 hover:bg-amber-100"
            onclick={loadSilent}
            title="Перевірити вручну чи прийшов webhook"
          ><span class="inline-flex items-center gap-1.5"><Icon name="refresh" size={12} /> Перевірити</span></button>
        {/if}
        <button
          class="rounded-lg px-3 py-1 text-xs font-medium bg-white border border-blue-200 hover:bg-blue-100"
          onclick={copyUrl}
          title="Копіювати URL"
        ><span class="inline-flex"><Icon name="copy" size={14} /></span></button>
      </div>
      {/if}

      <!-- Editor body: preview (embedded, read-only) ↔ edit (fullscreen). ONE SvelteFlow. -->
      <div class={isEdit ? 'fixed inset-0 z-[55] bg-[var(--color-bg)] flex flex-col p-3' : 'relative'}>
        {#if isEdit}
          <!-- Edit topbar -->
          <div class="flex items-center gap-2 mb-3 flex-none">
            <button
              class="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border-3)] bg-white px-3 py-2 text-xs font-semibold text-[var(--color-text)] hover:bg-[var(--color-bg-3)]"
              onclick={exitEdit} title="Вийти з редактора (у режим перегляду)"
            ><Icon name="close" size={14} /> Готово</button>
            <input
              bind:value={name}
              class="flex-1 min-w-0 rounded-lg border border-transparent hover:border-[var(--color-border-3)] px-2 py-1 text-base font-semibold focus:border-[var(--color-pu)] focus:bg-white"
            />
            {@render savedBadge()}
            <div class="flex items-center gap-1">
              <button
                class="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[var(--color-border-3)] bg-white text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)] disabled:opacity-40"
                onclick={undo} disabled={!canUndo} title="Скасувати (Ctrl/⌘+Z)"
              ><Icon name="restore" size={15} /></button>
              <button
                class="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[var(--color-border-3)] bg-white text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)] disabled:opacity-40 [transform:scaleX(-1)]"
                onclick={redo} disabled={!canRedo} title="Повернути (Ctrl/⌘+Shift+Z)"
              ><Icon name="restore" size={15} /></button>
              <div class="relative">
                <button
                  class="inline-flex items-center gap-1.5 h-8 rounded-lg border px-2.5 text-[11px] font-semibold transition-colors {versionsOpen ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]' : 'border-[var(--color-border-3)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-3)]'}"
                  onclick={openVersions} title="Історія версій"
                ><Icon name="audit" size={14} /> Версії{#if data}<span class="text-[var(--color-text-muted-2)]">· v{data.version}</span>{/if}</button>
                {#if versionsOpen}
                  <div class="fixed inset-0 z-[58]" role="presentation" onclick={() => (versionsOpen = false)}></div>
                  <div class="absolute right-0 top-full mt-1.5 z-[59] w-72 max-h-[60vh] overflow-y-auto rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl">
                    <div class="px-3 py-2 border-b border-[var(--color-border-3)] text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Історія версій</div>
                    {#if versions.length === 0}
                      <div class="px-3 py-4 text-center text-[11px] text-[var(--color-text-muted-2)] italic">Поки немає збережених версій.</div>
                    {:else}
                      {#each versions as v (v.id)}
                        <div class="flex items-center gap-2 px-3 py-2 hover:bg-[var(--color-bg-3)] text-xs">
                          <span class="font-semibold">v{v.version}</span>
                          {#if v.label}<span class="text-[10px] text-[var(--color-pu-3)]">{v.label}</span>{/if}
                          <span class="text-[10px] text-[var(--color-text-muted-2)] flex-1 truncate">{fmtDateTime(v.createdAt)}</span>
                          <button class="rounded border border-[var(--color-border-3)] px-2 py-0.5 text-[10px] font-semibold hover:border-[var(--color-pu-2)] hover:text-[var(--color-pu-3)]" onclick={() => onRestoreVersion(v.id)}>Відкотити</button>
                        </div>
                      {/each}
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
            {#if saveMsg}<span class="text-xs text-emerald-700">{saveMsg}</span>{/if}
            <button
              class="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
              disabled={testing || !data} onclick={runTest} title="Запустити тест"
            >{#if testing}<Icon name="refresh" size={14} /> Тест…{:else}<Icon name="play" size={14} /> Тест{/if}</button>
            <button
              class="rounded-lg px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
              style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
              disabled={saving || !dirty} onclick={save}
            >{#if saving}Збереження…{:else}<span class="inline-flex items-center gap-1.5"><Icon name="save" size={14} /> Зберегти</span>{/if}</button>
            {@render publishBtn()}
          </div>

          <!-- Вкладки повноекранного редактора: Редактор ↔ Історія запусків -->
          <div class="flex justify-center mb-3 flex-none">
            <div class="inline-flex items-center gap-1 rounded-xl bg-[var(--color-bg-3)] p-1">
              <button
                class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors {editTab === 'editor' ? 'bg-white shadow text-[var(--color-text)]' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}"
                onclick={() => setEditTab('editor')}
              ><Icon name="wrench" size={13} /> Редактор</button>
              <button
                class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors {editTab === 'runs' ? 'bg-white shadow text-[var(--color-text)]' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}"
                onclick={() => setEditTab('runs')}
              ><Icon name="audit" size={13} /> Історія запусків</button>
            </div>
          </div>
        {/if}

        <div class={isEdit ? 'flex-1 flex gap-3 min-h-0 relative' : 'flex gap-3 relative'}>
          {#if viewingRuns}
            <!-- Лівий борд: список запусків (вибір → перегляд на полотні) -->
            <aside class="w-[300px] flex-none rounded-2xl border border-[var(--color-border-3)] bg-white flex flex-col overflow-hidden">
              <div class="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--color-border-3)] flex-none">
                <Icon name="audit" size={14} />
                <span class="text-sm font-semibold flex-1">Запуски</span>
                <button class="inline-flex items-center justify-center w-7 h-7 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]" onclick={loadRuns} title="Оновити"><Icon name="refresh" size={14} /></button>
              </div>
              <div class="flex-1 overflow-y-auto">
                {#if runs.length === 0}
                  <div class="p-6 text-center text-xs text-[var(--color-text-muted)]">Запусків ще не було. Запусти «Тест» або надішли подію.</div>
                {:else}
                  <div class="divide-y divide-[var(--color-border-3)]">
                    {#each runs as r (r.id)}
                      <button
                        class="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors {viewExec?.id === r.id ? 'bg-purple-50' : 'hover:bg-[var(--color-bg-3)]'}"
                        onclick={() => pickRunInEditor(r.id)}
                      >
                        <span class="grid place-items-center w-6 h-6 rounded-full flex-none {r.status === 'success' ? 'bg-emerald-100 text-emerald-700' : r.status === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}">
                          {#if r.status === 'success'}<Icon name="check" size={13} />{:else if r.status === 'failed'}<Icon name="close" size={13} />{:else}<Icon name="statusPending" size={12} />{/if}
                        </span>
                        <div class="min-w-0 flex-1">
                          <div class="text-xs font-medium truncate">{fmtDateTime(r.occurredAt)}</div>
                          <div class="text-[10px] text-[var(--color-text-muted)]">{triggerLabel(r.triggerType)}{r.durationMs != null ? ` · ${r.durationMs} ms` : ''}</div>
                        </div>
                      </button>
                    {/each}
                  </div>
                {/if}
              </div>
            </aside>
          {/if}

          <!-- Canvas — ОДИН SvelteFlow, який переживає перемикання режиму -->
          <div
            class="rounded-2xl border border-[var(--color-border-3)] bg-white overflow-hidden relative flex-1 min-w-0"
            style={isEdit ? '' : 'height: calc(100vh - 280px); min-height: 500px;'}
            bind:this={canvasEl}
            role="application"
            ondragover={onCanvasDragOver}
            ondrop={onCanvasDrop}
          >
            <SvelteFlow
              bind:nodes={flowNodes}
              bind:edges={flowEdges}
              bind:viewport
              {nodeTypes}
              {edgeTypes}
              defaultEdgeOptions={{ type: 'automation', style: 'stroke:#7b4fbe; stroke-width:3;' }}
              fitView
              nodesDraggable={canEdit}
              nodesConnectable={canEdit}
              nodesFocusable={canEdit}
              elementsSelectable={true}
              elevateNodesOnSelect={canEdit}
              zoomOnDoubleClick={false}
              multiSelectionKey={['Shift', 'Meta', 'Control']}
              selectionKey={null}
              deleteKey={canEdit ? ['Backspace', 'Delete'] : null}
              onconnect={onConnect}
              onnodeclick={onNodeClick}
              onpaneclick={onPaneClick}
              onbeforedelete={onBeforeDelete}
              ondelete={onFlowDelete}
              onnodecontextmenu={onNodeContextMenu}
              onpanecontextmenu={onPaneContextMenu}
              onselectioncontextmenu={onSelectionContextMenu}
            >
              <Background />
              <Controls position="bottom-left" />
              <MiniMap />
              {#if !hasTrigger && !viewExec}
                <!-- «Додати тригер» у координатах полотна — зумиться/рухається з канвасом -->
                <ViewportPortal target="front">
                  <div style="position:absolute; left:0; top:0; transform: translate(-50%, -50%);">
                    <button
                      class="inline-flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[var(--color-pu-2)] bg-white/80 px-8 py-6 text-[var(--color-pu-3)] shadow-sm transition-colors {canEdit ? 'pointer-events-auto hover:bg-purple-50 hover:border-[var(--color-pu)] cursor-pointer' : 'pointer-events-none'}"
                      onclick={() => canEdit && (paletteOpen = true)}
                      disabled={!canEdit}
                    >
                      <span class="grid place-items-center w-12 h-12 rounded-full bg-[var(--color-pu)] text-white"><Icon name="add" size={26} /></span>
                      <span class="text-sm font-semibold">Додати тригер</span>
                      <span class="text-[11px] text-[var(--color-text-muted)]">{canEdit ? 'З чого почати автоматизацію' : 'Натисніть «Редагувати», щоб додати'}</span>
                    </button>
                  </div>
                </ViewportPortal>
              {/if}
            </SvelteFlow>

            {#if canEdit}
              <!-- Плаваюча кнопка «+» (зверху справа) — ховається коли відкрита панель нод -->
              {#if !paletteOpen}
                <button
                  class="absolute top-3 right-3 z-10 grid place-items-center w-10 h-10 rounded-full text-white shadow-lg hover:scale-105 transition-transform"
                  style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
                  onclick={() => (paletteOpen = true)}
                  title="Додати ноду"
                ><Icon name="add" size={22} /></button>
              {/if}
              <!-- Плаваюча кнопка «Розкласти» (знизу, праворуч від zoom-контролів) -->
              <button
                class="absolute bottom-3 left-[60px] z-10 inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border-3)] bg-white px-2.5 py-2 text-[11px] font-semibold text-[var(--color-text)] shadow-sm hover:bg-[var(--color-bg-3)]"
                onclick={autoLayout}
                title="Авто-розкладка графа"
              ><Icon name="sliders" size={14} /> Розкласти</button>
            {/if}

            {#if isPreview && !viewingRuns}
              <div class="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 rounded-full bg-black/55 text-white text-[11px] px-3 py-1 font-medium shadow z-10">
                Перегляд — натисніть «Редагувати» щоб змінювати
              </div>
            {/if}

          </div>

          {#if canEdit && paletteOpen}
            <!-- Права панель-шухляда (виїжджає справа): тригери або дії/логіка -->
            <aside
              class="absolute top-0 right-0 bottom-0 w-[320px] z-20 rounded-2xl border border-[var(--color-border-3)] bg-white flex flex-col overflow-hidden shadow-2xl"
              transition:fly={{ x: 340, duration: 220 }}
            >
              <div class="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--color-border-3)] flex-none">
                <span class="text-sm font-semibold flex-1">{hasTrigger ? 'Додати ноду' : 'Обрати тригер'}</span>
                <button class="inline-flex items-center justify-center w-7 h-7 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]" onclick={() => (paletteOpen = false)} title="Закрити"><Icon name="close" size={16} /></button>
              </div>
              <div class="relative p-3 pb-2 flex-none">
                <span class="absolute left-5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted-2)] inline-flex"><Icon name="search" size={13} /></span>
                <input bind:value={paletteSearch} placeholder="Пошук…" class="w-full rounded-lg border border-[var(--color-border-3)] bg-white pl-7 pr-2 py-1.5 text-xs" />
              </div>
              <div class="flex-1 overflow-y-auto px-3 pb-3 space-y-3">
                {#if !hasTrigger}
                  <div class="space-y-1.5">
                    {#each drawerTriggers as d (d.type)}
                      <button
                        class="w-full text-left rounded-lg border border-[var(--color-border-3)] p-2.5 hover:bg-[var(--color-bg-3)] hover:border-[var(--color-pu-2)] transition-colors"
                        onclick={() => addTriggerFromDrawer(d.type)}
                        title={d.description}
                      >
                        <div class="flex items-center gap-2.5">
                          <span class="grid place-items-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600"><Icon name={d.icon} size={17} /></span>
                          <div class="min-w-0">
                            <div class="text-xs font-semibold truncate">{d.label}</div>
                            <div class="text-[10px] text-[var(--color-text-muted)] truncate">{d.description}</div>
                          </div>
                        </div>
                      </button>
                    {/each}
                    {#if drawerTriggers.length === 0}<div class="text-[11px] text-[var(--color-text-muted-2)] italic px-1">Нічого не знайдено.</div>{/if}
                  </div>
                {:else}
                  {#each paletteGroups as grp (grp.category)}
                    <div>
                      <div class="px-0.5 pb-1 text-[9px] uppercase tracking-wider text-[var(--color-text-muted-2)] font-bold">{grp.category}</div>
                      <div class="space-y-1.5">
                        {#each grp.items as d (d.type)}
                          <button
                            class="w-full text-left rounded-lg border border-[var(--color-border-3)] p-2 hover:bg-[var(--color-bg-3)] hover:border-[var(--color-pu-2)] transition-colors cursor-grab active:cursor-grabbing"
                            draggable="true"
                            ondragstart={(e) => e.dataTransfer?.setData('application/automation', d.type)}
                            onclick={() => addNode(d.type)}
                            title={`${d.description} — клік або перетягни на полотно`}
                          >
                            <div class="flex items-center gap-2">
                              <span class="inline-flex text-[var(--color-text-muted)]"><Icon name={d.icon} size={16} /></span>
                              <span class="text-xs font-semibold truncate">{d.label}</span>
                            </div>
                          </button>
                        {/each}
                      </div>
                    </div>
                  {/each}
                  {#if paletteGroups.length === 0}<div class="text-[11px] text-[var(--color-text-muted-2)] italic px-1">Нічого не знайдено.</div>{/if}
                {/if}
              </div>
            </aside>
          {/if}
        </div>
      </div>

      {#snippet parametersPane(editable: boolean)}
        <div class="space-y-4">
          {#if !selectedNode}
            <div class="text-xs text-[var(--color-text-muted)] italic">Клікніть на ноду щоб редагувати її поля.</div>
          {:else}
            {@const def = $nodeCatalog[selectedNode.type]}
            <div class="flex items-center gap-2 mb-3">
              <div class="grid place-items-center w-8 h-8 rounded-lg bg-slate-100"><Icon name={def?.icon ?? 'automations'} size={17} /></div>
              <div class="text-sm font-semibold">{def?.label ?? selectedNode.type}</div>
            </div>
            <div class="text-[10px] text-[var(--color-text-muted)] mb-3">{def?.description ?? ''}</div>

            {#if selectedNode.type.startsWith('trigger.') && triggerDefs.length > 1}
              <div class="mb-3 rounded-lg border border-blue-200 bg-blue-50/60 p-2.5">
                <div class="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1.5">Тип тригера</div>
                <select
                  value={selectedNode.type}
                  disabled={!editable}
                  onchange={(e) => changeTriggerType((e.currentTarget as HTMLSelectElement).value)}
                  class="w-full rounded-lg border border-blue-200 bg-white px-2.5 py-1.5 text-xs disabled:opacity-60"
                >
                  {#each triggerDefs as t (t.type)}
                    <option value={t.type}>{t.label}</option>
                  {/each}
                </select>
                <div class="mt-1.5 text-[10px] text-[var(--color-text-muted)]">Зміна типу скидає параметри тригера.</div>
              </div>
            {/if}

            <div class="space-y-3">
              {#each fieldDefs as f (f.key)}
                {#if isFieldVisible(f)}
                  {@const value = readNodeParam(selectedNode, f.key)}
                  <div class="block relative">
                    <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-bold mb-1">
                      {f.label}{f.required ? ' *' : ''}
                    </div>
                    {#if f.type === 'select' && f.options}
                      <select
                        value={value}
                        disabled={!editable}
                        onchange={(e) => updateNodeParam(f.key, (e.currentTarget as HTMLSelectElement).value)}
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      >
                        <option value="">—</option>
                        {#each f.options as opt (opt.value)}
                          <option value={opt.value}>{opt.label}</option>
                        {/each}
                      </select>
                    {:else if f.type === 'credential'}
                      <div class="flex items-center gap-1">
                        <select
                          value={value}
                          disabled={!editable}
                          onchange={(e) => updateNodeParam(f.key, (e.currentTarget as HTMLSelectElement).value)}
                          class="flex-1 rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                        >
                          <option value="">— без авторизації —</option>
                          {#each credentials as cr (cr.id)}
                            <option value={cr.id}>{cr.name} · {cr.kind}</option>
                          {/each}
                        </select>
                        <button
                          type="button"
                          disabled={!editable}
                          class="inline-flex items-center justify-center h-[30px] w-[30px] rounded-lg border border-[var(--color-border-3)] bg-white text-[var(--color-pu-3)] hover:bg-[var(--color-bg-3)] disabled:opacity-50"
                          onclick={() => toggleCredForm(f.key)}
                          title="Новий креденшал"
                        ><Icon name={credFormForField === f.key ? 'close' : 'add'} size={14} /></button>
                      </div>
                      {#if credFormForField === f.key}
                        <div class="mt-2 rounded-lg border border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-2.5 space-y-2">
                          <input
                            bind:value={credName}
                            placeholder="Назва (напр. Stripe API)"
                            class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs"
                          />
                          <select
                            bind:value={credKind}
                            class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs"
                          >
                            <option value="bearer">Bearer token</option>
                            <option value="basic">Basic (логін/пароль)</option>
                            <option value="header">Header (ключ у заголовку)</option>
                            <option value="apiKeyQuery">API key (query-параметр)</option>
                            <option value="generic">Generic (довільні заголовки)</option>
                          </select>
                          <textarea
                            bind:value={credData}
                            rows="4"
                            placeholder={credPlaceholder[credKind]}
                            class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-[11px] font-mono resize-y"
                          ></textarea>
                          <div class="text-[10px] text-[var(--color-text-muted)]">Формат: {credPlaceholder[credKind]}</div>
                          <button
                            type="button"
                            class="w-full rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                            style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
                            disabled={credSaving || !credName.trim()}
                            onclick={() => submitCredential(f.key)}
                          >{credSaving ? 'Збереження…' : 'Зберегти креденшал'}</button>
                        </div>
                      {/if}
                    {:else if f.type === 'funnel'}
                      <select
                        value={value}
                        disabled={!editable}
                        onchange={(e) => { updateNodeParam(f.key, (e.currentTarget as HTMLSelectElement).value); updateNodeParam('stageSlug', ''); updateNodeParam('customEventKey', ''); }}
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      >
                        <option value="">— оберіть воронку —</option>
                        {#each funnelsList as fn (fn.slug)}
                          <option value={fn.slug}>{fn.name}</option>
                        {/each}
                      </select>
                    {:else if f.type === 'funnel_custom_event'}
                      {@const funnelVal = readNodeParam(selectedNode, f.dependsOn ?? 'funnelSlug')}
                      <select
                        value={value}
                        disabled={!editable || !funnelVal}
                        onchange={(e) => updateNodeParam(f.key, (e.currentTarget as HTMLSelectElement).value)}
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      >
                        <option value="">{funnelVal ? '— оберіть подію —' : '— спершу воронку —'}</option>
                        {#each customEventsByFunnel[funnelVal] ?? [] as ce (ce.key)}
                          <option value={ce.key}>{ce.name}</option>
                        {/each}
                      </select>
                    {:else if f.type === 'data_table'}
                      <!-- One-way value= + onchange (no bind:value feedback loop). The
                           async-load churn that used to desync this was moved to $effect. -->
                      <select
                        value={value}
                        disabled={!editable}
                        onchange={(e) => updateNodeParam(f.key, (e.currentTarget as HTMLSelectElement).value)}
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      >
                        <option value="">{f.hint ?? '— оберіть таблицю —'}</option>
                        {#each (f.customTablesOnly ? dataTablesList.filter((t) => !t.builtIn) : dataTablesList) as t (t.slug)}
                          <option value={t.slug}>{t.name}</option>
                        {/each}
                      </select>
                    {:else if f.type === 'record_fields'}
                      {@const tslug = readNodeParam(selectedNode, f.dependsOn ?? 'tableSlug')}
                      {@const tfields = (tableFieldsBySlug[tslug] ?? []).filter((x) => !x.is_computed)}
                      {@const obj = recObj(f.key)}
                      {@const usedKeys = Object.keys(obj)}
                      {@const avail = tfields.filter((x) => !usedKeys.includes(x.fieldKey))}
                      {#if !tslug}
                        <div class="text-[11px] italic text-[var(--color-text-muted-2)]">Спершу оберіть таблицю.</div>
                      {:else}
                        <div class="flex flex-col gap-1.5">
                          {#each usedKeys as k (k)}
                            {@const fd = tfields.find((x) => x.fieldKey === k)}
                            <div>
                              <div class="flex items-center gap-2">
                                <span class="w-28 shrink-0 truncate text-[11px] font-medium text-[var(--color-text-muted)]" title={fd?.label ?? k}>{fd?.label ?? k}</span>
                                {#if fd?.options && fd.options.length > 0}
                                  <select
                                    value={String(obj[k] ?? '')}
                                    disabled={!editable}
                                    onchange={(e) => recSet(f.key, k, (e.currentTarget as HTMLSelectElement).value)}
                                    class="flex-1 rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                                  >
                                    <option value="">— значення —</option>
                                    {#each fd.options as o (o)}<option value={o}>{o}</option>{/each}
                                  </select>
                                {:else}
                                  <input
                                    value={String(obj[k] ?? '')}
                                    disabled={!editable}
                                    oninput={(e) => recSet(f.key, k, (e.currentTarget as HTMLInputElement).value)}
                                    placeholder="значення / $.expr"
                                    class="flex-1 rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                                  />
                                  {#if editable}
                                    <button
                                      type="button"
                                      class="rounded-lg border border-[var(--color-border-3)] px-2 text-xs font-bold shrink-0 {hasAvailableData ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-slate-50 text-slate-400'}"
                                      style="height: 30px;"
                                      disabled={!hasAvailableData}
                                      onclick={() => openPicker(`${f.key}::${k}`)}
                                      title={hasAvailableData ? 'Вставити значення з вебхука / попередніх нод' : 'Немає доступних даних'}
                                    >$</button>
                                  {/if}
                                {/if}
                                <button type="button" disabled={!editable} onclick={() => recRemove(f.key, k)} title="Прибрати" class="text-red-500 hover:bg-red-50 rounded p-1 disabled:opacity-40"><Icon name="close" size={14} /></button>
                              </div>
                              {#if pickerForField === `${f.key}::${k}`}
                                <div class="relative mt-1">
                                  <PayloadFieldPicker payload={availableData} onPick={pickPath} onClose={() => (pickerForField = null)} />
                                </div>
                              {/if}
                            </div>
                          {/each}
                          {#if editable && avail.length > 0}
                            <select
                              value=""
                              onchange={(e) => { recAdd(f.key, (e.currentTarget as HTMLSelectElement).value); (e.currentTarget as HTMLSelectElement).value = ''; }}
                              class="mt-1 w-full rounded-lg border border-dashed border-[var(--color-border-3)] px-2.5 py-1.5 text-xs text-[var(--color-pu-3)] font-semibold"
                            >
                              <option value="">+ Додати поле</option>
                              {#each avail as fld (fld.fieldKey)}<option value={fld.fieldKey}>{fld.label}</option>{/each}
                            </select>
                          {/if}
                        </div>
                      {/if}
                    {:else if f.type === 'funnel_stage'}
                      {@const funnelVal = readNodeParam(selectedNode, f.dependsOn ?? 'funnelSlug')}
                      <select
                        value={value}
                        disabled={!editable || !funnelVal}
                        onchange={(e) => updateNodeParam(f.key, (e.currentTarget as HTMLSelectElement).value)}
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      >
                        <option value="">{funnelVal ? '— оберіть стейдж —' : '— спершу воронку —'}</option>
                        {#each stagesByFunnel[funnelVal] ?? [] as st (st.slug)}
                          <option value={st.slug}>{st.name}</option>
                        {/each}
                      </select>
                    {:else if f.type === 'person'}
                      {@const sel = personsList.find((p) => p.id === value)}
                      <input
                        value={sel ? sel.label : personSearch}
                        disabled={!editable}
                        oninput={(e) => (personSearch = (e.currentTarget as HTMLInputElement).value)}
                        placeholder="Пошук контакта… (порожньо = з події)"
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      />
                      {#if editable && personSearch && !sel}
                        <div class="mt-1 max-h-40 overflow-y-auto rounded-lg border border-[var(--color-border-3)] bg-white">
                          {#each filteredPersons.slice(0, 25) as p (p.id)}
                            <button type="button" onclick={() => { updateNodeParam(f.key, p.id); personSearch = ''; }}
                              class="block w-full text-left px-2.5 py-1.5 text-xs hover:bg-[var(--color-bg-3)]">{p.label}</button>
                          {/each}
                          {#if filteredPersons.length === 0}<div class="px-2.5 py-1.5 text-[11px] text-[var(--color-text-muted-2)] italic">Нічого не знайдено</div>{/if}
                        </div>
                      {/if}
                      {#if value}
                        <button type="button" disabled={!editable} onclick={() => { updateNodeParam(f.key, ''); personSearch = ''; }}
                          class="mt-1 text-[10.5px] text-[var(--color-pu-3)] hover:underline">очистити (брати з події)</button>
                      {/if}
                    {:else if f.type === 'boolean'}
                      <label class="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          disabled={!editable}
                          checked={readNodeRaw(selectedNode, f.key) === true}
                          onchange={(e) => updateNodeParam(f.key, (e.currentTarget as HTMLInputElement).checked)}
                        />
                        <span class="text-[var(--color-text-muted)]">{f.hint ?? 'Увімкнено'}</span>
                      </label>
                    {:else if f.type === 'json'}
                      <textarea
                        value={jsonText(readNodeRaw(selectedNode, f.key))}
                        disabled={!editable}
                        oninput={(e) => setJsonParam(f.key, (e.currentTarget as HTMLTextAreaElement).value)}
                        placeholder={f.hint}
                        rows="4"
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-[11px] font-mono resize-y disabled:opacity-60"
                      ></textarea>
                    {:else if f.type === 'number'}
                      <input
                        type="number"
                        value={value}
                        disabled={!editable}
                        oninput={(e) => {
                          const raw = (e.currentTarget as HTMLInputElement).value;
                          updateNodeParam(f.key, raw === '' ? '' : Number(raw));
                        }}
                        placeholder={f.hint}
                        class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs disabled:opacity-60"
                      />
                    {:else}
                      <div class="flex items-stretch gap-1">
                        <div class="flex-1">
                          {#if editable}
                            <ExpressionInput
                              value={value}
                              placeholder={f.hint}
                              payload={availableData}
                              onInput={(next) => updateNodeParam(f.key, next)}
                            />
                          {:else}
                            <div class="w-full rounded-lg border border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-2.5 py-1.5 text-xs font-mono break-all min-h-[30px] text-[var(--color-text-muted)]">{value || '—'}</div>
                          {/if}
                        </div>
                        {#if editable}
                          <button
                            type="button"
                            class="rounded-lg border border-[var(--color-border-3)] px-2 text-xs font-bold self-start {hasAvailableData ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-slate-50 text-slate-400'}"
                            style="height: 30px;"
                            onclick={() => openPicker(f.key)}
                            disabled={!hasAvailableData}
                            title={hasAvailableData ? 'Вставити значення з вебхука / попередніх нод' : 'Немає доступних даних (надішли тестовий webhook або додай ноду вище)'}
                          >$</button>
                        {/if}
                      </div>
                      {#if pickerForField === f.key}
                        <div class="relative">
                          <PayloadFieldPicker
                            payload={availableData}
                            onPick={pickPath}
                            onClose={() => (pickerForField = null)}
                          />
                        </div>
                      {/if}
                    {/if}
                  </div>
                {/if}
              {/each}
              {#if fieldDefs.length === 0}
                <!-- Trigger-нода: замість "немає полів" показуємо що прилетіло
                     з останнього webhook'а — щоб користувач бачив доступні
                     поля для наступних нод. -->
                {#if selectedNode.type === 'trigger.webhook'}
                  <div>
                    <div class="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-bold mb-2">
                      <Icon name="download" size={12} /> Поля з останнього webhook
                    </div>
                    {#if !livePayload}
                      <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                        <div class="inline-flex items-center gap-1.5 font-semibold mb-1"><Icon name="statusPending" size={13} /> Чекаємо першого запиту</div>
                        <div>Скопіюй webhook URL зверху та надішли на нього <code class="bg-white px-1 rounded">POST</code> із JSON-тілом. Як тільки запит прийде — тут зʼявиться структура полів.</div>
                        <div class="mt-2 text-[10px] opacity-80">
                          Приклад:
                          <pre class="bg-white border border-amber-200 rounded p-1.5 mt-1 font-mono whitespace-pre-wrap break-all">curl -X POST {webhookUrl} \
  -H "Content-Type: application/json" \
  -d '{'{'}"telegram_id":123,"amount":299{'}'}'</pre>
                        </div>
                      </div>
                    {:else}
                      <div class="text-[10px] text-[var(--color-text-muted)] mb-2">
                        Прийнято {data?.lastWebhookReceivedAt ? fmtDateTime(data.lastWebhookReceivedAt) : ''}.
                        У наступних нодах клікни <strong class="text-amber-700">$</strong> біля поля — і обери звідси шлях.
                      </div>
                      <PayloadFieldPicker
                        payload={livePayload}
                        onPick={(path) => navigator.clipboard.writeText(path)}
                        onClose={() => {}}
                        inline
                      />
                      <div class="mt-2 text-[10px] italic text-[var(--color-text-muted)]">Клік на поле копіює <code class="bg-slate-100 px-1 rounded">$.path</code> у буфер.</div>
                    {/if}
                  </div>
                {:else}
                  <div class="text-xs italic text-[var(--color-text-muted)]">У цієї ноди немає полів для налаштування.</div>
                {/if}
              {/if}
            </div>
          {/if}
        </div>
      {/snippet}

      <!-- INPUT/OUTPUT панель (NDV) -->
      {#snippet ioPane(label: string, value: unknown)}
        <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] font-bold mb-2">{label}</div>
        {#if ndvIoLoading}
          <div class="text-xs text-[var(--color-text-muted)] inline-flex items-center gap-1.5"><Icon name="refresh" size={13} /> Завантаження…</div>
        {:else if ndvStepError && label === 'OUTPUT'}
          <div class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700 font-mono whitespace-pre-wrap break-all">{ndvStepError}</div>
        {:else if value === undefined}
          <div class="rounded-lg border border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-3 text-[11px] text-[var(--color-text-muted)] leading-relaxed">
            Немає даних. Натисніть «Виконати воркфлоу» зверху — рушій виконує весь граф, і дані цього кроку зʼявляться тут.
          </div>
        {:else}
          <pre class="rounded-lg bg-slate-50 border border-[var(--color-border-3)] p-3 text-[11px] font-mono whitespace-pre-wrap break-all">{JSON.stringify(value, null, 2)}</pre>
        {/if}
      {/snippet}

      <!-- Node Detail View — модалка на весь екран із навігацією між блоками -->
      {#if ndvOpen && selectedNode}
        {@const ndvDef = $nodeCatalog[selectedNode.type]}
        <div
          class="fixed inset-0 z-[70] bg-black/60 flex items-stretch px-5 py-5"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          onkeydown={(e) => e.key === 'Escape' && closeNdv()}
        >
          <!-- Попередні блоки — вкладки-іконки біля лівого краю -->
          {#if ndvPrev.length > 0}
            <div class="absolute left-1 top-1/2 -translate-y-1/2 z-[72] flex flex-col gap-2">
              {#each ndvPrev as p (p.id)}
                <button
                  class="grid place-items-center w-9 h-9 rounded-full bg-white shadow-lg ring-1 ring-black/10 hover:ring-2 hover:ring-[var(--color-pu)] text-[var(--color-text)] transition-all"
                  onclick={() => navigateNdv(p.id)}
                  title={`← ${p.label}`}
                ><Icon name={p.icon} size={16} /></button>
              {/each}
            </div>
          {/if}

          <!-- Модальна панель (20px від країв): header + INPUT | Параметри | OUTPUT -->
          <div class="flex-1 min-w-0 rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
            <div class="flex-none flex items-center gap-3 px-5 py-3 border-b border-[var(--color-border-3)]">
              <div class="grid place-items-center w-8 h-8 rounded-lg bg-slate-100"><Icon name={ndvDef?.icon ?? 'automations'} size={17} /></div>
              <div class="min-w-0">
                <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] font-bold truncate">{ndvDef?.label ?? selectedNode.type}</div>
                <div class="text-sm font-semibold truncate">{selectedNode.type}</div>
              </div>
              <div class="flex-1"></div>
              {#if ndvStepStatus}
                <span class="rounded px-2 py-0.5 text-[10px] font-bold {ndvStepStatus === 'succeeded' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}">{ndvStepStatus === 'succeeded' ? 'успіх' : 'помилка'}</span>
              {/if}
              {#if !viewExec}
                <button
                  class="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  disabled={testing || !data}
                  onclick={runTest}
                  title="Виконати весь воркфлоу (рушій не вміє запускати окрему ноду)"
                >{#if testing}<Icon name="refresh" size={14} /> Виконання…{:else}<Icon name="play" size={14} /> Виконати воркфлоу{/if}</button>
              {/if}
              <button
                class="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[var(--color-text-muted)] hover:text-rose-600 hover:bg-[var(--color-bg-3)]"
                onclick={closeNdv}
                title="Закрити (Esc)"
              ><Icon name="close" size={18} /></button>
            </div>

            <div class="flex-1 grid grid-cols-[1fr_minmax(360px,1.4fr)_1fr] min-h-0">
              <section class="overflow-y-auto border-r border-[var(--color-border-3)] p-4">
                {@render ioPane('INPUT', ndvInput)}
              </section>
              <section class="overflow-y-auto p-4">
                {@render parametersPane(isEdit)}
              </section>
              <section class="overflow-y-auto border-l border-[var(--color-border-3)] p-4">
                {@render ioPane('OUTPUT', ndvOutput)}
              </section>
            </div>
          </div>

          <!-- Наступні блоки — вкладки-іконки біля правого краю -->
          {#if ndvNext.length > 0}
            <div class="absolute right-1 top-1/2 -translate-y-1/2 z-[72] flex flex-col gap-2">
              {#each ndvNext as n (n.id)}
                <button
                  class="grid place-items-center w-9 h-9 rounded-full bg-white shadow-lg ring-1 ring-black/10 hover:ring-2 hover:ring-[var(--color-pu)] text-[var(--color-text)] transition-all"
                  onclick={() => navigateNdv(n.id)}
                  title={`${n.label} →`}
                ><Icon name={n.icon} size={16} /></button>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
  {/if}

  <!-- Контекстне меню полотна/ноди -->
  {#if ctxMenu}
    {@const m = ctxMenu}
    <div class="fixed inset-0 z-[80]" role="presentation" onclick={closeCtxMenu} oncontextmenu={(e) => { e.preventDefault(); closeCtxMenu(); }}></div>
    <div class="fixed z-[81] w-[210px] rounded-xl border border-[var(--color-border-3)] bg-white shadow-2xl py-1 text-sm" style={ctxStyle(m)}>
      {#if m.kind === 'pane'}
        <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-3)] text-left" onclick={addNodeHere}><Icon name="add" size={15} /> Додати ноду тут</button>
        <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-3)] text-left" onclick={() => ctxRun(selectAll)}><Icon name="check" size={15} /> Виділити все</button>
        {#if clipboard}
          <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-3)] text-left" onclick={() => ctxRun(() => pasteClipboard(m.flowPos))}><Icon name="copy" size={15} /> Вставити</button>
        {/if}
      {:else}
        {#if m.targetId}
          <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-3)] text-left" onclick={() => ctxRun(() => openNodeById(m.targetId!))}><Icon name="edit" size={15} /> Відкрити</button>
        {/if}
        <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-3)] text-left" onclick={() => ctxRun(() => copyNodes(m.targetId))}><Icon name="copy" size={15} /> Скопіювати</button>
        <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-3)] text-left" onclick={() => ctxRun(() => duplicateNodes(m.targetId))}><Icon name="add" size={15} /> Дублювати</button>
        <div class="my-1 border-t border-[var(--color-border-3)]"></div>
        <button class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-rose-50 text-rose-600 text-left" onclick={() => ctxRun(() => deleteTargets(m.targetId))}><Icon name="close" size={15} /> Видалити</button>
      {/if}
    </div>
  {/if}
</AppShell>